import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Megaphone, BarChart3, Vault, Bell, Lock, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    name: "Marketing Command Center",
    description: "Unified campaigns, content, and outreach across every business.",
    icon: Megaphone,
    live: true,
    badge: "Live",
    cta: "Open Marketing Command Center →",
    to: "/marketing",
  },
  {
    name: "Ecosystem Analytics",
    description: "Cross-business performance insights and reporting in one place.",
    icon: BarChart3,
    live: true,
    badge: "Foundation · Release 1",
    detail: "Verified signed events power the Executive Command Center metrics.",
    cta: "Open Executive Command Center →",
    to: "/",
  },
  {
    name: "Integration Vault",
    description: "Securely store and manage API credentials and connections.",
    icon: Vault,
    live: true,
    badge: "Release 1 · Event connections",
    detail: "Per-business signing secrets (encrypted at rest), rotation, revocation and connection health.",
    cta: "Manage connections in Administration →",
    to: "/administration",
  },
  {
    name: "Unified Notifications",
    description: "A single inbox for alerts and events across all systems.",
    icon: Bell,
    live: true,
    badge: "Foundation · Release 1",
    detail: "Failed payments, alerts and connection errors appear in Needs Your Attention.",
    cta: "View attention items →",
    to: "/",
  },
];

export default function SharedServices() {
  return (
    <div className="max-w-7xl">
      <p className="text-xs uppercase tracking-widest text-[#581C87] font-semibold">
        Shared Services
      </p>
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-2">
        Ecosystem Services
      </h1>
      <p className="text-gray-500 mt-2">
        Cross-business capabilities that will connect your operating systems together.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-testid="shared-services-grid">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const testId = s.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

          if (s.live) {
            return (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
              >
                <Link
                  to={s.to}
                  className="group block bg-white border border-gray-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg hover:border-[#C4B5FD] transition-[transform,box-shadow,border-color] duration-200"
                  data-testid={`shared-service-card-${testId}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#581C87] to-[#7C3AED] flex items-center justify-center text-white">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-3 py-1 text-xs font-semibold text-[#047857]">
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                      {s.badge}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-lg tracking-tight text-gray-900 mt-5 flex items-center gap-2">
                    {s.name}
                    <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200 text-[#581C87]" />
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 leading-relaxed">{s.description}</p>
                  {s.detail && <p className="mt-2 text-xs text-gray-500" data-testid={`shared-service-detail-${testId}`}>{s.detail}</p>}
                  <p className="mt-3 text-xs font-medium text-[#581C87]">{s.cta}</p>
                </Link>
              </motion.div>
            );
          }

          return (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              className="relative bg-white border border-dashed border-gray-300 rounded-xl p-6 opacity-90"
              data-testid={`shared-service-card-${testId}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-12 w-12 rounded-xl bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF]">
                  <Icon className="h-6 w-6" />
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-3 py-1 text-xs font-semibold text-[#4B5563]">
                  <Lock className="h-3 w-3" />
                  Coming Later
                </span>
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight text-gray-700 mt-5">
                {s.name}
              </h3>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">{s.description}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
